import { useCallback, useEffect, useRef, useState } from "react";
import { RequireRole } from "../../auth/RequireRole";
import { ApiError, describeError } from "../../api/client";
import type { AdminAttachmentSummary } from "../../api/types";
import { ConfirmDialog } from "../../components/ConfirmDialog";
import { FeedbackPanel } from "../../components/FeedbackPanel";
import { Modal } from "../../components/Modal";
import { ModalCloseButton } from "../../components/ModalCloseButton";
import { deleteAttachment, listAttachments, uploadAttachment } from "./api";

type AttachmentsState =
  | { status: "loading" }
  | { status: "error"; message: string }
  | { status: "ready"; items: AdminAttachmentSummary[] };

/**
 * `FileReader.readAsDataURL` yields `data:<mime>;base64,<payload>` — the admin API's JSON+
 * Base64 upload contract (design.md A1) takes ONLY the payload, never the `data:` prefix.
 */
function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(reader.error ?? new Error("No se pudo leer el archivo."));
    reader.onload = () => {
      const result = reader.result;
      const text = typeof result === "string" ? result : "";
      const commaIndex = text.indexOf(",");
      resolve(commaIndex === -1 ? text : text.slice(commaIndex + 1));
    };
    reader.readAsDataURL(file);
  });
}

/**
 * Fase 6, Work Unit D (finding 15): the backend's guaranteed post-Base64 aggregate ceiling
 * is 27,000,000 bytes (design.md D2, `AdminAttachmentPolicy.MaxAggregateBase64Bytes`). Base64
 * grows raw bytes by 4/3 (rounded), so ANY single raw file already exceeding
 * `floor(27_000_000 * 3 / 4)` is guaranteed to be rejected regardless of what else is already
 * on the template. Checked against `file.size` BEFORE `fileToBase64`/`FileReader` ever reads
 * the file, so a huge PDF cannot freeze/exhaust the browser tab before the error is shown.
 */
const MAX_ATTACHMENT_RAW_BYTES = 20_250_000;

/**
 * One attachment's delete lifecycle — absent from the map when idle. Mirrors
 * `TemplatesScreen.tsx`'s established `SoftDeleteRowState`/`ReplaceRowState`
 * keyed-map pattern: before this slice, a single shared `uploadError` slot
 * tracked delete failures too (and there was no pending/disabled state at
 * all), so starting a delete on a DIFFERENT attachment while another was
 * still pending could clobber the first one's feedback, or double-fire a
 * duplicate delete on the same row. Keyed by `attachmentId`
 * (`Record<string, DeleteAttachmentRowState>`) so concurrent deletes on
 * different attachments track independently; every update below uses the
 * functional `setState` form so two racing transitions can never clobber one
 * another regardless of resolution order.
 */
type DeleteAttachmentRowState = { status: "pending" } | { status: "error"; message: string };

/**
 * redesign-panel-hubspot-mailgun, PR 15 slice D (task 15.1/15.3, design.md
 * §5.5 "Destructive soft-delete/attachment delete actions require
 * confirmation and pending feedback"): same `window.confirm`-based guard
 * already used for soft-delete (`TemplatesScreen.tsx#buildSoftDeleteConfirmation`).
 * Attachment delete is ALWAYS a soft-delete server-side too (design.md A7,
 * `api.ts#deleteAttachment`) — the copy says so explicitly.
 */
function buildDeleteAttachmentConfirmation(fileName: string): string {
  return `¿Borrar el adjunto "${fileName}"? Se marcará como eliminado y no podrá adjuntarse en nuevos envíos.`;
}

interface TemplateAttachmentsPanelProps {
  templateId: string;
  onClose: () => void;
}

/**
 * Attachment upload/list/delete panel (plantilla-a3-adjuntos-pdf, Fase 4).
 * Extracted out of `TemplatesScreen.tsx` verbatim (redesign-panel-hubspot-mailgun,
 * PR 15 slice D, task 15.1/15.4). Self-contained — mirrors `TemplateCreateForm`'s
 * extraction convention (own state, own module-level `./api` calls) rather
 * than `TemplateReplaceEditor`'s controlled one, since nothing OUTSIDE this
 * component needs visibility into its internal state (unlike the replace
 * editor's dirtiness, which `confirmLeave()` needs to observe).
 *
 * List rendering is unconditional — `Consulta` reads it read-only
 * (`admin-panel-ui` "Consulta ve adjuntos de solo lectura"); the upload input
 * and each row's delete button are `RequireRole="Operador"`-gated, same as
 * the pre-extraction inline panel.
 *
 * Task 15.4 "keep attachment concurrency logic feature-local": `useMutation`
 * (`state/useMutation.ts`) has ZERO built-in sequencing protection, so the
 * `requestSeqRef` monotonic-sequencing guard below stays hand-rolled here,
 * PRESERVED VERBATIM from the pre-extraction `TemplatesScreen.tsx` — 4
 * documented rounds of race-condition fixes (finding-12, R9/R10, Bug 1/2/3,
 * see the inline comments on each operation below) depend on this exact
 * mechanism; it is not simplified, "cleaned up", or reimplemented here.
 *
 * Changing only the `templateId` PROP (switching from one template's panel
 * to another without closing first) does NOT unmount/remount this component
 * at its tree position — `requestSeqRef` below is a SINGLE ref instance that
 * persists across that switch, which is exactly what lets it reject a
 * still-in-flight response from the template being switched away from. The
 * `useEffect` on `[templateId]` is what resets this panel's local state and
 * refetches on a switch; a SEPARATE cleanup-only effect bumps the ref on
 * true unmount, mirroring the pre-extraction `closeAttachments()`'s explicit
 * bump ("even though nothing is currently rendered to show it").
 */
export function TemplateAttachmentsPanel({ templateId, onClose }: TemplateAttachmentsPanelProps) {
  const [attachmentsState, setAttachmentsState] = useState<AttachmentsState>({ status: "loading" });
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [deleteAttachmentState, setDeleteAttachmentState] = useState<Record<string, DeleteAttachmentRowState>>({});
  // #177: window.confirm() replaced with the in-app ConfirmDialog.
  const [pendingDeleteAttachment, setPendingDeleteAttachment] = useState<{
    attachmentId: string;
    fileName: string;
  } | null>(null);
  // Round-2 re-review (findings R9/R10, superseding Work Unit D's finding-12 per-template
  // ref): a single monotonic request-sequence ref, bumped synchronously at the START of
  // EVERY attachment-related async operation (list refresh, upload, AND delete). Each
  // operation captures its own sequence number locally and only commits its result if the
  // ref still holds that exact number when the operation resolves. This rejects a response
  // from a DIFFERENT template (switching templates or closing the panel bumps the ref too)
  // AND rejects an out-of-order OLDER response for the SAME template (a newer operation
  // already bumped the ref past the older one's captured number) — one mechanism for both
  // races.
  const requestSeqRef = useRef(0);

  const refreshAttachments = useCallback(async (forTemplateId: string) => {
    // Captured synchronously BEFORE the first `await` — this is what makes ordering
    // deterministic: two overlapping calls to `refreshAttachments` (even for the same
    // template) get strictly increasing sequence numbers in call order, regardless of
    // which one's underlying `listAttachments` promise happens to settle first.
    const requestId = ++requestSeqRef.current;
    try {
      const items = await listAttachments(forTemplateId);
      // R9/R10: discard a response whose sequence number is no longer current — either a
      // NEWER operation for a different template already superseded it (finding 12), or a
      // NEWER refresh for the SAME template already superseded it (finding R9, out-of-order
      // network resolution of two same-template requests).
      if (requestSeqRef.current !== requestId) return;
      setAttachmentsState({ status: "ready", items });
    } catch (error) {
      if (requestSeqRef.current !== requestId) return;
      setAttachmentsState({ status: "error", message: describeError(error, "No se pudieron cargar los adjuntos.") });
    }
  }, []);

  useEffect(() => {
    // Mirrors the pre-extraction `openAttachments(templateId)`: reset this panel's local
    // state and refetch whenever the operator opens this panel OR switches to a DIFFERENT
    // template without closing it first (a `templateId` prop change alone never remounts
    // this component at its tree position).
    setUploadError(null);
    setDeleteAttachmentState({});
    setAttachmentsState({ status: "loading" });
    void refreshAttachments(templateId);
  }, [templateId, refreshAttachments]);

  useEffect(() => {
    // Mirrors the pre-extraction `closeAttachments()`'s explicit bump: on true unmount
    // (operator closes the panel entirely), bump the sequence so any still in-flight
    // request can never commit its result later, even though nothing is rendered anymore
    // to show it.
    return () => {
      requestSeqRef.current += 1;
    };
  }, []);

  // User follow-up 2026-09-18 ("Ver adjuntos"/"Reemplazar contenido" deben
  // abrirse como un popup, con estilo consistente con el resto del panel):
  // the panel used to render as a bare, unstyled `<aside>` with its own
  // hand-rolled Escape-to-close/Tab-trap/focus-restoration effect — replaced
  // by the shared `<Modal>` primitive (`components/Modal.tsx`), which already
  // generalizes `ConfirmDialog`'s same contract, so this component no longer
  // owns that logic itself.
  const handleUploadAttachment = useCallback(
    async (file: File) => {
      setUploadError(null);
      // Fase 6, Work Unit D (finding 15): checked BEFORE fileToBase64/FileReader ever reads
      // the file — a huge PDF must never freeze/exhaust the tab before this error shows.
      if (file.size > MAX_ATTACHMENT_RAW_BYTES) {
        setUploadError(
          `El archivo supera el tamano maximo permitido por adjunto (~${Math.floor(MAX_ATTACHMENT_RAW_BYTES / 1_000_000)} MB).`,
        );
        return;
      }
      // R10: captured synchronously here, at the START of this upload operation — mirrors
      // `refreshAttachments`'s own sequence capture. If the operator switches templates (or
      // closes the panel) before this upload settles, `requestSeqRef.current` will have
      // moved past `requestId` by the time the rejection below resolves, and the stale error
      // must never be attributed to whatever template/panel is now current.
      const requestId = ++requestSeqRef.current;
      try {
        const contenidoBase64 = await fileToBase64(file);
        await uploadAttachment(templateId, {
          nombreArchivo: file.name,
          // Round-3 re-review (Bug 3): the file picker/drop zone is PDF-only by
          // construction, so `file.type` (a browser/OS guess that can be an empty
          // string OR a non-empty alias like "application/x-pdf"/
          // "application/octet-stream") is never round-tripped. The backend's own
          // magic-byte check remains the real validation.
          mimeType: "application/pdf",
          contenidoBase64,
        });
        // Round-3 re-review (Bug 1): the post-upload refresh itself bumps
        // requestSeqRef and would "win" any race against a newer operation for a
        // DIFFERENT template unless we first confirm this upload's own captured
        // requestId is still current — mirrors the check already used below in
        // the error path.
        if (requestSeqRef.current !== requestId) return;
        await refreshAttachments(templateId);
      } catch (error) {
        if (requestSeqRef.current !== requestId) return;
        // El 413 agregado ya llega con mensaje explicito de "30 MB" desde ./api
        // (task 4.4) — nunca un fallo generico o silencioso.
        setUploadError(error instanceof ApiError ? error.message : "No se pudo subir el adjunto.");
      }
    },
    [templateId, refreshAttachments],
  );

  const performDeleteAttachment = useCallback(
    async (attachmentId: string) => {
      // Captured synchronously here, mirroring handleUploadAttachment's own guard — this
      // requestId is what keeps the SHARED list refresh below scoped to only the most
      // current attachment operation on this template (finding-12/R9/R10/Bug1/Bug2 — the
      // guard's ORIGINAL job, unchanged).
      const requestId = ++requestSeqRef.current;
      // Functional updates throughout (never a bare replace) — concurrent deletions on
      // DIFFERENT attachments must merge into the map, never clobber each other, same
      // pattern established for SoftDeleteRowState/ReplaceRowState.
      setDeleteAttachmentState((prev) => ({ ...prev, [attachmentId]: { status: "pending" } }));
      try {
        await deleteAttachment(templateId, attachmentId);
        // Codex automated review on PR #153 (P2, `TemplateAttachmentsPanel.tsx:224`): this
        // ROW's own completion must commit UNCONDITIONALLY, even if a concurrent delete on
        // a DIFFERENT attachment has since advanced the shared `requestSeqRef` past this
        // request's own captured id. Gating this on the ref (as the success/error commits
        // used to be) silently discarded THIS row's own bookkeeping whenever a sibling row's
        // later operation "won" the race — leaving the row stuck `pending` forever (if this
        // one succeeded) or hiding its own failure (if this one errored). Only the SHARED
        // list refresh below stays sequence-gated; each row's local state is independent of
        // every other row and must always reflect its OWN request's own outcome.
        setDeleteAttachmentState((prev) =>
          Object.fromEntries(Object.entries(prev).filter(([id]) => id !== attachmentId)),
        );
        if (requestSeqRef.current !== requestId) return;
        await refreshAttachments(templateId);
      } catch (error) {
        // Fase 6, Work Unit D (finding 13): mirrors handleUploadAttachment's error-handling
        // pattern — a 409 (attachment referenced by a scheduled/sending campaign, A7 guard)
        // or any other failure must surface to the operator, never fail silently or produce
        // an unhandled promise rejection. Now shown inline against THIS ROW only (keyed by
        // attachmentId), never a shared banner that a different row's action could clobber.
        // Codex PR #153 finding: this commit is intentionally UNGUARDED by `requestSeqRef`
        // (see the success-path comment above) — this row's own failure must always surface,
        // even once a sibling row's later operation has advanced the shared sequence. A
        // stale error surviving a TEMPLATE switch is still impossible: the `useEffect` on
        // `[templateId]` resets `deleteAttachmentState` to `{}` on every switch, and this
        // attachmentId key would not match any row in whatever template is now displayed
        // even if it somehow arrived after that reset.
        setDeleteAttachmentState((prev) => ({
          ...prev,
          [attachmentId]: {
            status: "error",
            message: error instanceof ApiError ? error.message : "No se pudo borrar el adjunto.",
          },
        }));
      }
    },
    [templateId, refreshAttachments],
  );

  // #177: entry point for the "Eliminar" button — only opens the in-app
  // ConfirmDialog; the actual mutation fires from confirmDeleteAttachment.
  const requestDeleteAttachment = useCallback(
    (attachmentId: string, fileName: string) => {
      // Guards a duplicate submission for the SAME attachment even if the disabled button
      // somehow still dispatched a second click (defense in depth, same pattern
      // `TemplatesScreen`'s soft-delete guard uses).
      if (deleteAttachmentState[attachmentId]?.status === "pending") return;
      setPendingDeleteAttachment({ attachmentId, fileName });
    },
    [deleteAttachmentState],
  );

  const confirmDeleteAttachment = useCallback(() => {
    if (!pendingDeleteAttachment) return;
    const { attachmentId } = pendingDeleteAttachment;
    setPendingDeleteAttachment(null);
    void performDeleteAttachment(attachmentId);
  }, [pendingDeleteAttachment, performDeleteAttachment]);

  const cancelDeleteAttachment = useCallback(() => {
    setPendingDeleteAttachment(null);
  }, []);

  // Design handoff v2 (Panel Precision App v2.dc.html, "ADJUNTOS DE PLANTILLA"):
  // header with × / body with one card per attachment (PDF icon + name) and a
  // dashed "Subir PDF" drop zone / footer with Cerrar. Every handler, the
  // per-row delete state and the request-sequence guard are unchanged.
  return (
    <Modal ariaLabel="Adjuntos de plantilla" onClose={onClose}>
      <div className="v2-modal-v2 v2-modal-v2--narrow v2-template-attachments">
      <header className="v2-modal-v2__header">
        <h2>Adjuntos de plantilla</h2>
        <ModalCloseButton onClose={onClose} />
      </header>

      <div className="v2-modal-v2__body">
      {attachmentsState.status === "loading" && <p role="status">Cargando adjuntos…</p>}
      {attachmentsState.status === "error" && (
        <FeedbackPanel
          variant="error"
          message={attachmentsState.message}
          onRetry={() => void refreshAttachments(templateId)}
        />
      )}
      {attachmentsState.status === "ready" && (
        <>
          {attachmentsState.items.length === 0 ? (
            <p className="empty-state v2-template-attachments__empty">Sin adjuntos todavía.</p>
          ) : (
            <ul className="v2-template-attachments__list" aria-label="Adjuntos">
              {attachmentsState.items.map((attachment) => {
                // Per-row pending/error state, keyed by attachmentId (unchanged).
                const rowDeleteState = deleteAttachmentState[attachment.attachmentId];
                const isDeletingThisRow = rowDeleteState?.status === "pending";
                const deleteErrorForThisRow = rowDeleteState?.status === "error" ? rowDeleteState.message : null;
                return (
                  <li key={attachment.attachmentId} className="v2-template-attachments__item">
                    <span className="v2-template-attachments__icon" aria-hidden="true">
                      PDF
                    </span>
                    <span className="v2-template-attachments__name">{attachment.nombreArchivo}</span>
                    <RequireRole role="Operador">
                      <button
                        type="button"
                        className="v2-u-target-min v2-template-attachments__delete"
                        onClick={() => requestDeleteAttachment(attachment.attachmentId, attachment.nombreArchivo)}
                        disabled={isDeletingThisRow}
                        aria-busy={isDeletingThisRow}
                      >
                        Quitar
                      </button>
                      {isDeletingThisRow && <p role="status" className="v2-template-attachments__row-note">Borrando…</p>}
                      {deleteErrorForThisRow && (
                        <p role="alert" className="v2-template-attachments__row-note">
                          {deleteErrorForThisRow}
                        </p>
                      )}
                    </RequireRole>
                  </li>
                );
              })}
            </ul>
          )}

          <RequireRole role="Operador">
            <label htmlFor="attachment-file" className="v2-dropzone">
              <span className="v2-template-attachments__upload-icon" aria-hidden="true">
                ↑
              </span>
              <span>
                <span className="v2-dropzone__title">Subir PDF</span>
                <br />
                <span className="v2-dropzone__meta">Solo archivos .pdf</span>
              </span>
              <input
                id="attachment-file"
                type="file"
                accept="application/pdf"
                onChange={(event) => {
                  const file = event.target.files?.[0];
                  event.target.value = "";
                  if (file) {
                    void handleUploadAttachment(file);
                  }
                }}
              />
            </label>
          </RequireRole>
        </>
      )}

      {uploadError && <p role="alert">{uploadError}</p>}

      {pendingDeleteAttachment && (
        <ConfirmDialog
          message={buildDeleteAttachmentConfirmation(pendingDeleteAttachment.fileName)}
          onConfirm={confirmDeleteAttachment}
          onCancel={cancelDeleteAttachment}
        />
      )}
      </div>

      <footer className="v2-modal-v2__footer">
        <button type="button" className="v2-u-target-min v2-btn-secondary" onClick={onClose}>
          Cerrar
        </button>
      </footer>
      </div>
    </Modal>
  );
}
